/**
 * A document's open work as two counts: findings in red (a compliance gap
 * someone has to decide) and suggestions in neutral (housekeeping). One red
 * number for both made a suggested rename read as a finding.
 *
 * @module components/regulation/policy/OpenWorkBadges
 */
import { RadarIcon, SparklesIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import type { OpenWork } from "./openWorkByPolicy";

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function OpenWorkBadges({
  work,
  emptyLabel = "None",
  className,
}: {
  readonly work: OpenWork;
  /** Shown when nothing waits: "None" in a table cell, "—" in a compact one. */
  readonly emptyLabel?: string;
  readonly className?: string;
}) {
  if (work.findings === 0 && work.suggestions === 0) {
    return <span className="text-xs text-muted-foreground">{emptyLabel}</span>;
  }
  // Each badge is `relative` so its sr-only label (absolutely positioned) is
  // placed inside it: otherwise, in a horizontally scrolled table, the label
  // escapes the scroller and widens the whole page.
  return (
    <span className={cn("flex w-fit items-center gap-1.5", className)}>
      {work.findings > 0 ? (
        <span
          title={plural(work.findings, "open finding", "open findings")}
          data-open-findings={work.findings}
          className="relative inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium tabular-nums text-destructive"
        >
          <RadarIcon aria-hidden className="size-3" />
          {work.findings}
          <span className="sr-only"> {work.findings === 1 ? "open finding" : "open findings"}</span>
        </span>
      ) : null}
      {work.suggestions > 0 ? (
        <span
          title={plural(work.suggestions, "suggestion waiting", "suggestions waiting")}
          data-open-suggestions={work.suggestions}
          className="relative inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground"
        >
          <SparklesIcon aria-hidden className="size-3" />
          {work.suggestions}
          <span className="sr-only"> {work.suggestions === 1 ? "suggestion waiting" : "suggestions waiting"}</span>
        </span>
      ) : null}
    </span>
  );
}
